import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    useCallback,
} from 'react';
import {
    onAuthStateChanged,
    signInAnonymously,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signInWithPopup,
    sendPasswordResetEmail,
    signOut,
} from 'firebase/auth';
import { auth, googleProvider } from '../firebaseApp';

/**
 * Single source of truth for authentication across the whole app.
 *
 * Two tiers of identity:
 *   - GUEST: an anonymous Firebase user. Created automatically whenever nobody
 *     is signed in. Anonymous users still satisfy `request.auth != null`, so
 *     the storefront (Shares, Promos, product image uploads) keeps working
 *     exactly as before — no visitor ever sees a login wall just to create a map.
 *   - ACCOUNT: a real user (email/password or Google). Required only to *save*
 *     maps to "My Maps".
 *
 * `account` is the signed-in real user (or null for guests). `requireAccount()`
 * is what the Save button calls: it resolves immediately if there's already an
 * account, otherwise it pops the sign-in dialog and resolves once the user
 * signs in (true) or dismisses it (false).
 */
const AuthContext = createContext(null);

// The app used to auto-sign every visitor into this single shared service
// account. That session is persisted in returning browsers, so on boot we sign
// it out and fall back to an anonymous guest — otherwise existing visitors
// would appear "signed in" as the shared account and could save maps under one
// shared identity.
const LEGACY_SHARED_EMAIL = 'geo-shaped@geo-shaped.com';

// Map Firebase's terse error codes to friendly, on-brand copy.
const FRIENDLY_ERRORS = {
    'auth/invalid-email': 'That email address looks invalid.',
    'auth/user-disabled': 'This account has been disabled.',
    'auth/user-not-found': 'No account found with that email.',
    'auth/wrong-password': 'Incorrect email or password.',
    'auth/invalid-credential': 'Incorrect email or password.',
    'auth/email-already-in-use': 'An account with that email already exists.',
    'auth/weak-password': 'Please choose a password of at least 6 characters.',
    'auth/popup-closed-by-user': 'Google sign-in was cancelled.',
    'auth/cancelled-popup-request': 'Google sign-in was cancelled.',
    'auth/popup-blocked': 'Your browser blocked the sign-in popup. Allow popups and try again.',
    'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
    'auth/network-request-failed': 'Network error. Check your connection and try again.',
};

export const friendlyAuthError = (err) =>
    (err && FRIENDLY_ERRORS[err.code]) || (err && err.message) || 'Something went wrong. Please try again.';

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(auth.currentUser);
    const [authReady, setAuthReady] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);

    // Pending requireAccount() resolver — settled when an account signs in or
    // the dialog is dismissed. Held in a ref so the auth listener can reach it.
    const pendingResolve = useRef(null);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (nextUser) => {
            if (!nextUser) {
                // Nobody signed in — create/restore a guest session so the
                // storefront keeps its write access. Errors here are non-fatal
                // (read-only features still work).
                signInAnonymously(auth).catch((err) => {
                    // eslint-disable-next-line no-console
                    console.warn('Anonymous sign-in failed:', err);
                });
                return;
            }

            // Migrate a stale legacy shared-account session to a guest session.
            if (nextUser.email === LEGACY_SHARED_EMAIL) {
                signOut(auth); // listener re-fires with null → anonymous guest
                return;
            }
            setUser(nextUser);
            setAuthReady(true);

            // A real account just signed in — satisfy any pending gate and
            // close the dialog (covers both the Save-gated and the plain
            // "Log in" menu path).
            if (!nextUser.isAnonymous) {
                if (pendingResolve.current) {
                    pendingResolve.current(true);
                    pendingResolve.current = null;
                }
                setDialogOpen(false);
            }
        });
        return unsub;
    }, []);

    const account = user && !user.isAnonymous ? user : null;
    const isSignedIn = Boolean(account);

    const loginEmail = useCallback(
        (email, password) => signInWithEmailAndPassword(auth, email.trim(), password),
        []
    );
    const signupEmail = useCallback(
        (email, password) => createUserWithEmailAndPassword(auth, email.trim(), password),
        []
    );
    const loginGoogle = useCallback(() => signInWithPopup(auth, googleProvider), []);
    const resetPassword = useCallback((email) => sendPasswordResetEmail(auth, email.trim()), []);

    const logout = useCallback(async () => {
        // After sign-out the listener immediately restores an anonymous guest
        // session, so the storefront never loses write access.
        await signOut(auth);
    }, []);

    const openAuthDialog = useCallback(() => setDialogOpen(true), []);

    const closeAuthDialog = useCallback(() => {
        setDialogOpen(false);
        if (pendingResolve.current) {
            pendingResolve.current(false);
            pendingResolve.current = null;
        }
    }, []);

    /**
     * Ensure there's a signed-in account before a gated action (e.g. Save).
     * Resolves true if signed in (now or already), false if the user dismisses
     * the dialog.
     */
    const requireAccount = useCallback(() => {
        if (isSignedIn) return Promise.resolve(true);
        return new Promise((resolve) => {
            pendingResolve.current = resolve;
            setDialogOpen(true);
        });
    }, [isSignedIn]);

    const value = useMemo(
        () => ({
            user,
            account,
            isSignedIn,
            authReady,
            dialogOpen,
            loginEmail,
            signupEmail,
            loginGoogle,
            resetPassword,
            logout,
            requireAccount,
            openAuthDialog,
            closeAuthDialog,
        }),
        [
            user,
            account,
            isSignedIn,
            authReady,
            dialogOpen,
            loginEmail,
            signupEmail,
            loginGoogle,
            resetPassword,
            logout,
            requireAccount,
            openAuthDialog,
            closeAuthDialog,
        ]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
    return ctx;
};

export default AuthContext;
